(function () {
  if (customElements.get('urgency-bar')) return;

  customElements.define(
    'urgency-bar',
    class UrgencyBar extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.layout === 'bar' ? 'bar' : 'bubble';

        /* A fixed element is positioned against the nearest ancestor carrying a
           transform, filter or perspective - not the viewport - so one styled
           section wrapper is enough to leave the bar narrower than the screen
           and its contents off centre. Moving it to the body makes the viewport
           the containing block again. The theme does the same for its own
           overlays via ModalComponent's requiresBodyAppended.

           Re-entrant: appendChild disconnects and reconnects the element, so
           this returns and lets the second pass do the real work. */
        if (this.layout === 'bar' && this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }
        // Two of these in the bubble layout: one in the pill, one in the panel.
        this.timerEls = this.querySelectorAll('[data-urgency-timer]');
        this.launcher = this.querySelector('[data-urgency-toggle]');
        this.panel = this.querySelector('[data-urgency-panel]');
        this.codeButton = this.querySelector('[data-urgency-copy]');
        this.dismissButton = this.querySelector('[data-urgency-dismiss]');
        this.storageKey = 'tidal:urgency-bar-dismissed';
        this.seenKey = 'tidal:urgency-bar-seen';
        this.copiedLabel = this.dataset.copiedLabel || 'Copied';
        this.timeZone = this.dataset.timezone || 'America/New_York';

        // An unknown zone name throws on first use; fall back rather than
        // leaving the countdown stuck on its placeholder.
        try {
          new Intl.DateTimeFormat('en-US', { timeZone: this.timeZone }).format();
        } catch (error) {
          this.timeZone = 'America/New_York';
        }

        // The cart bar ignores an earlier dismissal on purpose: it is the last
        // screen before paying, so the offer gets stated rather than offered.
        if (this.layout === 'bubble' && this.dismissedToday()) {
          this.remove();
          return;
        }

        this.codeButton?.addEventListener('click', this.copyCode.bind(this));

        if (this.layout === 'bar') {
          this.setUpBar();
        } else {
          this.setUpBubble();
        }

        this.setAttribute('data-ready', '');
      }

      /* The bar sits in the flow's way, so it reserves its own height on the
         body - otherwise it would cover the checkout button, which is the one
         thing on that page that must stay reachable. It wraps to two lines on
         narrow phones, so the height is not a constant the stylesheet can
         hardcode. */
      setUpBar() {
        this.publishHeight();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.publishHeight.bind(this));
          this.resizeObserver.observe(this);
        }
      }

      setUpBubble() {
        this.launcher?.addEventListener('click', this.toggle.bind(this));
        this.dismissButton?.addEventListener('click', this.dismiss.bind(this));

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.close();
        };

        // A pill in the corner is easy to ignore, so the offer introduces
        // itself once per session and is just a pill on every page after that.
        if (this.dataset.autoOpen === 'true' && !this.seenThisSession()) {
          this.autoOpenTimeout = setTimeout(() => {
            this.open();
          }, Number(this.dataset.autoOpenDelay || 3) * 1000);
        }
      }

      publishHeight() {
        document.documentElement.style.setProperty('--urgency-bar-height', this.offsetHeight + 'px');
      }

      disconnectedCallback() {
        clearTimeout(this.autoOpenTimeout);
        this.resizeObserver?.disconnect();
        document.documentElement.style.removeProperty('--urgency-bar-height');
        if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      }

      /* --- open / close ----------------------------------------------------- */

      /* The launcher works both ways, so the panel can be put away without
         dismissing the offer for the rest of the day. */
      toggle() {
        if (this.hasAttribute('data-open')) {
          this.close();
        } else {
          this.open();
        }
      }

      open() {
        if (!this.panel || this.hasAttribute('data-open')) return;
        clearTimeout(this.autoOpenTimeout);
        clearTimeout(this.closeTimeout);
        this.markSeen();

        this.panel.hidden = false;
        // The panel is display:none while hidden, so the transition needs a
        // laid-out start value before the open state lands. Reading offsetHeight
        // forces that synchronously - deferring to requestAnimationFrame would
        // leave the panel displayed but transparent whenever frames are starved
        // (background tab, reduced motion), which reads as a dead widget.
        void this.panel.offsetHeight;
        this.setAttribute('data-open', '');

        this.launcher?.setAttribute('aria-expanded', 'true');
        // Deferred: this same click would otherwise reach the document handler
        // and close the panel in the tick it opened.
        setTimeout(() => {
          document.addEventListener('click', this.onDocumentClick);
          document.addEventListener('keydown', this.onKeydown);
        }, 0);
      }

      close() {
        if (!this.hasAttribute('data-open')) return;
        this.removeAttribute('data-open');
        this.launcher?.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);

        // Left in the flow until the transition has run, then taken out so it
        // cannot be tabbed into.
        this.closeTimeout = setTimeout(() => {
          if (this.panel) this.panel.hidden = true;
        }, 250);
      }

      /* --- code ------------------------------------------------------------- */

      async copyCode() {
        const code = this.codeButton.dataset.urgencyCopy;
        if (!code) return;

        try {
          await navigator.clipboard.writeText(code);
        } catch (error) {
          // Clipboard access needs a secure context and can be refused outright.
          // The code is on screen either way, so a failed copy is not worth an
          // error state - just skip the confirmation.
          return;
        }

        const original = this.codeButton.textContent;
        this.codeButton.textContent = this.copiedLabel;
        this.codeButton.classList.add('urgency-widget__code--copied');
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.codeButton.textContent = original;
          this.codeButton.classList.remove('urgency-widget__code--copied');
        }, 2000);
      }

      /* --- persistence ------------------------------------------------------ */

      /* Dismissal lasts for the day, not forever: the offer renews at midnight,
         so tomorrow's visit should see tomorrow's widget. */
      dismiss() {
        try {
          localStorage.setItem(this.storageKey, this.today());
        } catch (error) {
          // Private mode. It closes for this page view and comes back on the
          // next one, which is a fair outcome for a promo widget.
        }
        this.remove();
      }

      dismissedToday() {
        if (window.Shopify && window.Shopify.designMode) return false;
        try {
          return localStorage.getItem(this.storageKey) === this.today();
        } catch (error) {
          return false;
        }
      }

      seenThisSession() {
        if (window.Shopify && window.Shopify.designMode) return false;
        try {
          return sessionStorage.getItem(this.seenKey) === '1';
        } catch (error) {
          return false;
        }
      }

      markSeen() {
        try {
          sessionStorage.setItem(this.seenKey, '1');
        } catch (error) {
          // Private mode: the panel introduces itself again on the next page.
        }
      }

      // Keyed to the shop's day, matching the countdown - otherwise a visitor
      // abroad gets the widget back in the middle of the same offer.
      today() {
        return new Intl.DateTimeFormat('en-CA', { timeZone: this.timeZone }).format(new Date());
      }
    }
  );
})();
